import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Play } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@scio3/ui';

interface WistiaDialogProps {
  videoId: string | null;
  onClose: () => void;
}

/**
 * Wistia video dialog that displays a video in a modal overlay.
 * Used by GetStartedChecklist for the "getting started" video.
 */
export function WistiaDialog({ videoId, onClose }: WistiaDialogProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);

  const handleLoad = useCallback(() => setIsLoading(false), []);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        setIsLoading(true);
        onClose();
      }
    },
    [onClose],
  );

  return (
    <Dialog open={!!videoId} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="welcome-video-dialog"
        className="w-[min(1280px,90vw)] max-w-none"
        aria-describedby={undefined}
      >
        <DialogHeader variant="muted">
          <DialogTitle className="flex items-center gap-2">
            <Play className="h-5 w-5" />
            {t('getStarted.watchVideo')}
          </DialogTitle>
        </DialogHeader>
        {videoId && (
          <div className="relative aspect-video w-full">
            {isLoading && (
              <div
                className="absolute inset-0 flex items-center justify-center bg-muted rounded-md"
                role="status"
                aria-label={t('Loading...')}
              >
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            )}
            <iframe
              src={`https://fast.wistia.com/embed/iframe/${videoId}?autoplay=1`}
              className="aspect-video w-full rounded-md"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
              title={t('getStarted.watchVideo')}
              onLoad={handleLoad}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
